import { ChevronUpIcon, RootState } from '@umanforge/common';
import TimeGridSelectorTileComponent from './timegrid_selector_tile.component';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TimeGrid } from 'interfaces/timegrid.interface';
import { useDispatch, useSelector } from 'react-redux';
import { TimeGridService } from 'services/timeGrid.service';
import { TimeGridIcon } from 'components/icons/timegrid.icon';
import { SettingService } from 'services/setting.service';


interface TimeGridSelectorProps {
  className?: string;
  sectorId: string;
  canBeEmpty?: boolean;
  onTimeGridChange: ( timegrid: TimeGrid ) => void
}

const TimeGridSelectorComponent = ( {
  className = '',
  sectorId,
  canBeEmpty = false,
  onTimeGridChange
} : TimeGridSelectorProps ) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const buttonRef = useRef<HTMLDivElement | null>( null );
  const panelRef = useRef<HTMLDivElement | null>( null );
  //const logger: LoggerService = new LoggerService( 'TimeGridSelector Component' );
  const [ isFirstRender, setIsFirstRender ] = useState( true );
  const [ open, setOpen ] = useState<boolean>( false );
  const [ timegrids, setTimeGrids ] = useState<TimeGrid[]>( [] );
  const [ selectedTimeGrid, setSelectedTimeGrid ] = useState<TimeGrid | undefined>( );

  useEffect( () => {
    init();
  }, [] );

  useEffect( () => {
    fetchTimeGrids();
  }, [ canBeEmpty ] );

  useEffect( () => {
    const handleClickOutside = ( event: MouseEvent ) => {
      if ( !open ) {
        return;
      }

      const target = event.target as Node;
      if ( buttonRef.current?.contains( target ) ) {
        return;
      }

      if ( panelRef.current && !panelRef.current.contains( target ) ) {
        setOpen( false );
      }
    };

    document.addEventListener( 'mousedown', handleClickOutside );

    return () => {
      document.removeEventListener( 'mousedown', handleClickOutside );
    };
  }, [ open ] );

  const init = async () => {
    if ( isFirstRender ) {
      await fetchTimeGrids();
      setIsFirstRender( false );
    }
  };

  const fetchTimeGrids = async () => {
    if ( sectorId ) {
      const _timegrids: TimeGrid[] = [];
      const fecthedTimegrids = await TimeGridService.getEnableTimeGridsBySectorId( sectorId, token, dispatch );

      if ( canBeEmpty ) {
        const settings = await SettingService.getSectorSettingsBySector( sectorId, token, dispatch );
        if ( settings && settings.calendarEndHour && settings.calendarStartHour ) {
          _timegrids.push( {
            id: 'no_timegrid',
            name: t( 'translation:no_timegrid_selected' ),
            timeStart: '1970-01-01 ' + settings.calendarStartHour.value + ':00',
            timeEnd: '1970-01-01 ' + settings.calendarEndHour.value + ':00',
            breaktimes: [],
            idSector: sectorId,
            disabled: false,
          } as TimeGrid );
        }
        else {
          _timegrids.push( {
            id: 'no_timegrid',
            name: t( 'translation:no_timegrid_selected' ),
            timeStart: '1970-01-01 00:00',
            timeEnd: '1970-01-01 23:59',
            breaktimes: [],
            idSector: sectorId,
            disabled: false,
          } as TimeGrid );
        }
      }
      _timegrids.push( ...fecthedTimegrids );
      setTimeGrids( _timegrids );
      if ( _timegrids.length > 0 ) {
        setSelectedTimeGrid( _timegrids[ 0 ] );
        onTimeGridChange( _timegrids[ 0 ] );
      }
    }
    else {
      setTimeGrids( [] );
    }
  };

  const handleTimegridChange = ( timegrid: TimeGrid ) => {
    setSelectedTimeGrid( timegrid );
    onTimeGridChange( timegrid );
    setOpen( false );
  };

  if ( timegrids.length == 0 ) {
    return <></>;
  }

  return (
    <div className={ className + ' w-full h-full p-1' }>
      <div className='relative py-1 flex justify-center'>
        <div
          ref={ buttonRef }
          id={'timegrid_selector_button'}
          className={'flex items-center gap-2 justify-between w-fit px-5 py-2 rounded-xl border border-sky-300 bg-sky-100 hover:bg-sky-200 transition-colors duration-150 font-medium text-slate-700 cursor-pointer'}
          onClick={ () => { setOpen( !open ); } }
        >
          <TimeGridIcon className='w-5 h-5 text-slate-500' />
          <label htmlFor={'timegrid_selector_button'} className='w-fit px-2 text-center whitespace-nowrap'>
            { selectedTimeGrid ? selectedTimeGrid.name : t( 'translation:select_timegrids' ) }
          </label>

          <ChevronUpIcon className={ `${ open && 'rotate-180' } w-4 h-4 text-slate-500 transition-transform` } />
        </div>
        { open &&
          <div
            ref={ panelRef }
            className='absolute left-1/2 z-50 mt-14 w-[22rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-[0_12px_28px_rgba(15,23,42,0.08)] animate-[fadeInUp_0.2s_cubic-bezier(0.2,0.8,0.2,1)]'
            onMouseDown={ event => { return event.stopPropagation(); } }
          >
            <div className='space-y-1.5'>
              { timegrids.map( ( timegrid, index ) => {
                return <TimeGridSelectorTileComponent
                  key={ index + '_' + timegrid.id }
                  timegrid={ timegrid }
                  selected={ selectedTimeGrid?.id == timegrid.id }
                  onClick={ handleTimegridChange }
                />;
              } ) }
            </div>
          </div>
        }
      </div>

    </div>
  );
};

export default TimeGridSelectorComponent;